import React, { useEffect, useState, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import Chip from '@mui/material/Chip';
import './ImageViewer.css';
import { AuthContext } from '../../../AuthContext';
import { editImage, getImageById, getImageFileUrl } from '../../../services/mediaService';
import ThumbnailItemComponent, { ThumbnailItem } from '../ThumbnailItem/ThumbnailItemComponent';

const options = { timeZone: 'Europe/Budapest', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' };

const ImageViewer = ({ currentImage, images, onClose, categories, onMediaUpdated, prevPage, nextPage }) => {

  const navigate = useNavigate();

  const [imageItem, setImageItem] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [isRotated, setIsRotated] = useState(false);
  const [token] = useState(localStorage.getItem('token') || '');
  const slideshowRef = useRef(null); // Ref for the slideshow container

  const dataFromStorage = images;

  const level = localStorage.getItem('level');


  useEffect(() => {
    const fetchImageDetails = async () => {
      try {
        const fetchedImage = currentImage;
        setImageItem(fetchedImage);
        const newImageUrl = getImageFileUrl(fetchedImage.id);
        setImageUrl(newImageUrl);

        setImageLoaded(false);
        const img = new Image();
        img.src = newImageUrl;
        img.onload = () => setImageLoaded(true);
        img.onerror = () => {
          message.error('Failed to load image.');
          setImageLoaded(false);
        };
      } catch (error) {
        console.error('Failed to fetch image details:', error);
        message.error('Failed to load image details.');
        setImageItem(null);
        setImageUrl(null);
      }
    };

    fetchImageDetails();
  }, [currentImage]);

  useEffect(() => {
    setRotation(0);
    setIsRotated(false);
  }, [currentImage]);

  // Scroll to active thumbnail
  useEffect(() => {
    if (slideshowRef.current && imageItem) {
      const activeThumbnail = slideshowRef.current.querySelector(`.slideshow-thumbnail.active`);
      if (activeThumbnail) {
        activeThumbnail.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }
  }, [imageItem]);


  const getCategoryNames = (itemCategory, fallbackCategory) => {
    const itemCategories = [];
    let raw = itemCategory;
    if ((!raw || raw === '') && fallbackCategory) raw = fallbackCategory;

    if (Array.isArray(raw)) {
      raw.forEach((element) => {
        const catId = parseInt(element);
        const category = categories.find((cat) => cat.id === catId);
        itemCategories.push(category ? category.name : 'Ismeretlen kategória');
      });
      return itemCategories.length ? itemCategories : ['Ismeretlen kategória'];
    }

    if (typeof raw === 'string' && raw.trim().startsWith('[')) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return getCategoryNames(parsed);
        }
      } catch (e) {
        // fallthrough to treat as comma-separated
      }
    }

    if (typeof raw === 'string' && raw.includes(',')) {
      const parts = raw.split(',').map((s) => s.trim()).filter(Boolean);
      parts.forEach((element) => {
        const catId = parseInt(element);
        const category = categories.find((cat) => cat.id === catId);
        itemCategories.push(category ? category.name : 'Ismeretlen kategória');
      });
      return itemCategories.length ? itemCategories : ['Ismeretlen kategória'];
    }

    if (raw) {
      const catId = parseInt(raw);
      if (!isNaN(catId)) {
        const category = categories.find((cat) => cat.id === catId);
        itemCategories.push(category ? category.name : 'Ismeretlen kategória');
        return itemCategories;
      }

      itemCategories.push(raw);
      return itemCategories;
    }

    return ['Ismeretlen kategória'];
  };

  const handleImageClick = (id) => {

    const clickedItem = dataFromStorage.find((img) => img.id === id);
    if (clickedItem) {
      setImageItem(clickedItem);
      setImageUrl(getImageFileUrl(clickedItem.id));

    }
  };

  const rotateLeft = () => {
    setRotation((prevRotation) => prevRotation - 90);
    setIsRotated(true);
  };

  const rotateRight = () => {
    setRotation((prevRotation) => prevRotation + 90);
    setIsRotated(true);
  };

  const handleStoreRotation = async () => {
    try {
      await editImage(token, imageItem.id, { rotation: rotation });
      message.success('Image rotation stored successfully!');
      setRotation(0);
      setIsRotated(false);
      if (onMediaUpdated) {
        onMediaUpdated();
      }
    } catch (error) {
      console.error('Failed to store image rotation:', error);
      message.error('Failed to store image rotation.');
    }
  };

  if (!imageItem) {
    return <div className="image-viewer-container">Loading...</div>;
  }

  return (
    <div className="image-viewer-container">
      <div className="image-viewer-header">
        <h2>{imageItem.name}</h2>
        <button onClick={onClose} className="close-viewer-btn">Close</button>
      </div>
      <div className="image-viewer-content">
        <div className="image-main-area"> {/* New container for image and slideshow */}
          <div className="image-display-area">
            {!imageLoaded ? (
              <span>Töltés...</span>
            ) : (
              <img
                src={imageUrl}
                alt={imageItem.name}
                className="full-size-image"
                style={{ transform: `rotate(${rotation}deg)` }}
              />
            )}
            <div className="rotate-buttons">
              <button onClick={rotateLeft}><i className="bi bi-arrow-counterclockwise"></i></button>
              <button onClick={rotateRight}><i className="bi bi-arrow-clockwise"></i></button>
            </div>
          </div>
          {/*<div className="thumbnail-slideshow" ref={slideshowRef}>
            {dataFromStorage.map((item) => (
              <ThumbnailItemComponent
                key={item.id}
                item={item}
                size={80}
                imageItem={imageItem}
                onClick={() => handleImageClick(item.id)}
              />
            ))}
          </div>*/}
          <div className="row align-items-center">
            <div className="col-1 d-flex justify-content-center">
              <button className="btn btn-light" onClick={prevPage}>
                <i className="bi bi-chevron-double-left"></i>
              </button>
            </div>

            <div className="col-10">
              <div className="thumbnail-slideshow" ref={slideshowRef}>
                {dataFromStorage.map((item) => (
                  <ThumbnailItemComponent
                    key={item.id}
                    item={item}
                    size={80}
                    imageItem={imageItem}
                    onClick={() => handleImageClick(item.id)}
                  />
                ))}
              </div>
            </div>

            <div className="col-1 d-flex justify-content-center">
              <button className="btn btn-light" onClick={nextPage}>
                <i className="bi bi-chevron-double-right"></i>
              </button>
            </div>
          </div>
        </div>
        <div className="image-details-sidebar">
          <p><strong>Készítés ideje:</strong> {imageItem.created_time ? new Date(imageItem.created_time * 1000).toLocaleDateString('hu-HU', options) : '-'}</p>
          <p><strong>Feltöltés ideje:</strong> {new Date(imageItem.upload_time * 1000).toLocaleDateString('hu-HU', options)}</p>
          <p><strong>Leírás:</strong> {imageItem.comment}</p>
          <p><strong>Kategóriák:</strong></p>
          <div className="category-chips">
            {getCategoryNames(imageItem.categories, imageItem.category).map((categoryName, index) => (
              <Chip key={index} label={categoryName} style={{ margin: '4px', backgroundColor: 'red' }} />
            ))}
          </div>
          <a href={imageUrl} target="_blank" rel="noreferrer" className="dm-btn">Open Original</a>
          {level > 2 && isRotated && (
            <button onClick={handleStoreRotation} className="store-rotation-btn">Store Rotation</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageViewer;
